import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { cn } from '@Utils/cn';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BotMessageSquare,
  Bug,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  Plus,
  Send,
  Square,
  Trash2,
} from 'lucide-react';
import cookies from 'browser-cookies';
import { requireTenantId } from '../../stores/tenantStore';
import {
  useAiChats,
  useAiStarterSuggestions,
  useAiFollowUpSuggestions,
  aiChatKeys,
} from '@Hooks/useAiChats';
import { useSouls } from '@Hooks/useSouls';
import { useEvalSuites } from '@Hooks/useEvals';
import service, { uri_ai_chat } from '@Service';
import type { AiChatMessage, BusinessObjectMap, StreamEvent } from '@Types/soul';
import { SuggestionPills, SuggestionPillsSkeleton } from '@Components/SuggestionPills';
import type { CardStatus } from './ActionCard';
import type { ActionStateEntry } from './MessageContent';
import BusinessObjectPanel from './BusinessObjectPanel';
import DraftEventPanel from './DraftEventPanel';
import MessageContent from './MessageContent';
import TypingDots from './TypingDots';
import ThinkingIndicator from './ThinkingIndicator';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { toastSuccess, toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useLocation, useNavigate } from 'react-router-dom';

interface LocalMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  reasoning?: string;
  isActionResult?: boolean;
  thinking?: boolean;
}

export default function AIChat() {
  const queryClient = useQueryClient();
  const { data: chats, status: chatsStatus } = useAiChats({ limit: 50 });
  const { data: souls } = useSouls();
  const location = useLocation();
  const navigate = useNavigate();

  // Chat state
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [selectedSoulId, setSelectedSoulId] = useState<string>('');
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasStartedChatFromState = useRef(false);

  // Business objects state
  const [businessObjects, setBusinessObjects] = useState<BusinessObjectMap>({});
  const [boPanelOpen, setBoPanelOpen] = useState(false);

  // Debug mode
  const [debugMode, setDebugMode] = useState(false);
  const [debugLogs, setDebugLogs] = useState<Array<{ type: 'tool-call' | 'tool-result'; toolName: string; data: any; timestamp: number }>>([]);

  // Persisted action card states (survives re-renders / navigation)
  const [actionStates, setActionStates] = useState<Record<string, ActionStateEntry>>({});

  // Prompt suggestions. Starter pills (empty chat) are cached per-Soul; follow-up
  // pills are cached per chat + last assistant message, so they're generated once
  // per turn and reused until a new message produces a new reply.
  const { data: starterSuggestions, isLoading: starterLoading } =
    useAiStarterSuggestions({ soulId: selectedSoulId });

  // The last completed assistant message identifies the current turn; it keys
  // (and caches) the follow-up suggestions until the next reply arrives.
  const lastMessage = messages[messages.length - 1];
  const lastAssistantMessageId =
    lastMessage?.role === 'assistant' && lastMessage.content
      ? lastMessage.id
      : undefined;
  const { data: followUps, isLoading: followUpsLoading } =
    useAiFollowUpSuggestions({
      soulId: selectedSoulId,
      chatId: activeChatId ?? undefined,
      lastAssistantMessageId,
      enabled: !isStreaming,
    });

  // Make this page fill the viewport by overriding Layout's <main> padding and scroll
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const main = containerRef.current?.closest('main') as HTMLElement | null;
    const scrollParent = main?.parentElement as HTMLElement | null;
    if (!main || !scrollParent) return;

    const saved = {
      mainPad: main.style.padding,
      mainOverflow: main.style.overflow,
      mainHeight: main.style.height,
      mainDisplay: main.style.display,
      mainFlexDir: main.style.flexDirection,
      parentOverflow: scrollParent.style.overflow,
    };

    main.style.padding = '0';
    main.style.overflow = 'hidden';
    main.style.height = '100%';
    main.style.display = 'flex';
    main.style.flexDirection = 'column';
    scrollParent.style.overflow = 'hidden';

    return () => {
      main.style.padding = saved.mainPad;
      main.style.overflow = saved.mainOverflow;
      main.style.height = saved.mainHeight;
      main.style.display = saved.mainDisplay;
      main.style.flexDirection = saved.mainFlexDir;
      scrollParent.style.overflow = saved.parentOverflow;
    };
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Set default soul on load
  useEffect(() => {
    if (souls?.length && !selectedSoulId && !hasStartedChatFromState.current) {
      const defaultSoul = souls.find((s) => s.isDefault);
      if (defaultSoul) {
        setSelectedSoulId(defaultSoul.id);
      }
    }
  }, [souls, selectedSoulId]);

  // Load chat messages when selecting a chat from history
  const loadChat = useCallback(
    async (chatId: string) => {
      setActiveChatId(chatId);
      try {
        const [data, bos] = await Promise.all([
          service.getAiChat(chatId),
          service.getBusinessObjects(chatId),
        ]);
        const loaded: LocalMessage[] = data.messages.map((msg: AiChatMessage) => {
          const reasoningPart = Array.isArray(msg.parts)
            ? msg.parts.find((p: any) => p.type === 'reasoning')
            : null;
          const isActionResult = Array.isArray(msg.parts)
            ? msg.parts.some((p: any) => p.type === 'action-result')
            : false;
          return {
            id: msg.id,
            role: msg.role as 'user' | 'assistant',
            content: msg.content ?? '',
            ...(reasoningPart ? { reasoning: reasoningPart.text } : {}),
            ...(isActionResult ? { isActionResult: true } : {}),
          };
        });
        // Build action states from action-result messages
        const states: Record<string, ActionStateEntry> = {};
        for (const msg of data.messages) {
          if (!Array.isArray(msg.parts)) continue;
          for (const part of msg.parts) {
            if (part.type === 'action-result' && part.sourceMessageId != null && part.actionIndex != null) {
              const key = `${part.sourceMessageId}:${part.actionIndex}`;
              // Process in order so retries overwrite previous state
              states[key] = {
                status: part.status === 'error' ? 'error' : 'success',
                result: part.text || '',
                ...(part.result?.eventId ? { eventId: part.result.eventId } : {}),
              };
            }
          }
        }

        setMessages(loaded);
        setActionStates(states);
        setBusinessObjects(bos);
        if (data.chat.soulId) {
          setSelectedSoulId(data.chat.soulId);
        }
      } catch (error) {
        console.error('Error loading chat:', error);
      }
    },
    [],
  );

  const startNewChat = useCallback(() => {
    setActiveChatId(null);
    setMessages([]);
    setBusinessObjects({});
    setActionStates({});
    setInputValue('');
    inputRef.current?.focus();
  }, []);

  const deleteChat = useCallback(
    async (chatId: string) => {
      try {
        await service.deleteAiChat(chatId);
        await queryClient.invalidateQueries({ queryKey: aiChatKeys.all });
        if (activeChatId === chatId) {
          startNewChat();
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    },
    [activeChatId, queryClient, startNewChat],
  );

  const sendMessage = useCallback(async (message: string = null, soulIdArg: string = null) => {
    const text = message ? message.trim() : inputValue.trim();
    if (!text || isStreaming) return;

    const userMsg: LocalMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');

    // Build messages payload for API
    // When chatId exists, the API already has history — only send the new message
    const allMessages = activeChatId
      ? [{ role: userMsg.role, content: userMsg.content }]
      : [...messages, userMsg].map((m) => ({
          role: m.role,
          content: m.content,
        }));

    setIsStreaming(true);
    const abortController = new AbortController();
    abortRef.current = abortController;

    // Add placeholder assistant message
    const assistantId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant', content: '' },
    ]);

    try {
      const accessToken = cookies.get('at-admin') || cookies.get('at');
      const soulId = soulIdArg ? soulIdArg : selectedSoulId;
      const res = await fetch(uri_ai_chat, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${accessToken}`,
          'tenant-id': requireTenantId(),
        },
        body: JSON.stringify({
          messages: allMessages,
          ...(soulId ? { soulId: soulId } : {}),
          ...(activeChatId ? { chatId: activeChatId } : {}),
          ...(debugMode ? { debug: true } : {}),
        }),
        signal: abortController.signal,
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || `HTTP ${res.status}`);
      }

      // Capture chat ID from response header
      const chatIdFromHeader = res.headers.get('x-chat-id');
      if (chatIdFromHeader && !activeChatId) {
        setActiveChatId(chatIdFromHeader);
      }

      // Read the NDJSON stream
      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let fullText = '';
      let fullReasoning = '';
      let lineBuffer = '';
      if (debugMode) setDebugLogs([]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        // Keep the last (possibly incomplete) line in the buffer
        lineBuffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event: StreamEvent = JSON.parse(line);
            switch (event.type) {
              case 'reasoning':
                fullReasoning += event.content;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, reasoning: fullReasoning } : m,
                  ),
                );
                break;
              case 'text':
                fullText += event.content;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, content: fullText } : m,
                  ),
                );
                break;
              case 'bo-update':
                setBusinessObjects((prev) => ({
                  ...prev,
                  [event.data.key]: event.data,
                }));
                break;
              case 'omni-progress':
                // The progress payload (task lists / "Analyzing data...") isn't useful to show.
                // Flag the message as thinking so the bubble renders a thinking animation until
                // the real response streams in.
                setMessages((prev) =>
                  prev.map((m) => (m.id === assistantId ? { ...m, thinking: true } : m)),
                );
                break;
              case 'done':
                setBusinessObjects(event.businessObjects);
                if (event.messageId) {
                  const realId = event.messageId;
                  setMessages((prev) =>
                    prev.map((m) => (m.id === assistantId ? { ...m, id: realId } : m)),
                  );
                }
                break;
              case 'tool-call':
                setDebugLogs((prev) => [...prev, { type: 'tool-call', toolName: event.toolName, data: event.args, timestamp: Date.now() }]);
                break;
              case 'tool-result':
                setDebugLogs((prev) => [...prev, { type: 'tool-result', toolName: event.toolName, data: event.result, timestamp: Date.now() }]);
                break;
            }
          } catch {
            // Skip malformed lines
          }
        }
      }

      // Process any remaining data in buffer
      if (lineBuffer.trim()) {
        try {
          const event: StreamEvent = JSON.parse(lineBuffer);
          if (event.type === 'reasoning') {
            fullReasoning += event.content;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, reasoning: fullReasoning } : m,
              ),
            );
          } else if (event.type === 'text') {
            fullText += event.content;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                return { ...m, content: fullText };
              }),
            );
          } else if (event.type === 'bo-update') {
            setBusinessObjects((prev) => ({
              ...prev,
              [event.data.key]: event.data,
            }));
          } else if (event.type === 'omni-progress') {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, thinking: true } : m)),
            );
          } else if (event.type === 'done') {
            setBusinessObjects(event.businessObjects);
            if (event.messageId) {
              const realId = event.messageId;
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, id: realId } : m)),
              );
            }
          }
        } catch {
          // Skip malformed data
        }
      }

      // Refresh chat list
      queryClient.invalidateQueries({ queryKey: aiChatKeys.all });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // User stopped the stream
      } else {
        console.error('Stream error:', error);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: `Error: ${error.message || 'Something went wrong'}`,
                }
              : m,
          ),
        );
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      // Clear the thinking flag so the animation never lingers (e.g. on abort with no text).
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, thinking: false } : m)));
    }
  }, [inputValue, isStreaming, messages, selectedSoulId, activeChatId, queryClient]);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleActionExecuted = useCallback((
    msg: { id: string; content: string },
    sourceMessageId?: string,
    actionIndex?: number,
    status?: CardStatus
  ) => {
    // Update action states map for persistence across renders
    if (sourceMessageId != null && actionIndex != null && status) {
      const key = `${sourceMessageId}:${actionIndex}`;
      setActionStates((prev) => ({
        ...prev,
        [key]: { status, result: msg.content },
      }));
    }

    // Add the action result pill to messages (only for success with valid msg id)
    if (msg.id) {
      setMessages((prev) => [...prev, {
        id: msg.id,
        role: 'user' as const,
        content: msg.content,
        isActionResult: true,
      }]);
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    const { initialMessage, soulId, chatId } = location.state ?? {};

    if (chatId) {
      loadChat(chatId);
    } else if (initialMessage && !hasStartedChatFromState.current) {
      hasStartedChatFromState.current = true;
      // clear location state
      navigate(location.pathname, {
        replace: true,
        state: null,
      });

      setSelectedSoulId(soulId);

      startNewChat();
      sendMessage(initialMessage, soulId);
    }
  }, []);

  return (
    <div ref={containerRef} className="flex h-full w-full">
      {/* Sidebar */}
      <div className="flex w-64 shrink-0 flex-col border-r border-zinc-200 bg-zinc-50">
        <div className="border-b border-zinc-200 p-3">
          <Button
            variant="outline"
            className="w-full"
            onClick={startNewChat}
          >
            <Plus className="mr-2 size-4" />
            New Chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {chatsStatus === 'pending' ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="size-4 animate-spin text-zinc-400" />
            </div>
          ) : !chats?.length ? (
            <div className="p-4 text-center text-sm text-zinc-400">
              No conversations yet
            </div>
          ) : (
            <div className="space-y-0.5 p-2">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  className={cn(
                    'group flex items-center justify-between rounded-md px-3 py-2 text-sm cursor-pointer transition-colors',
                    activeChatId === chat.id
                      ? 'bg-zinc-200 text-zinc-900'
                      : 'text-zinc-600 hover:bg-zinc-100',
                  )}
                  onClick={() => loadChat(chat.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{chat.title}</div>
                    <div className="text-xs text-zinc-400">
                      {new Date(chat.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="ml-2 shrink-0 rounded p-1 opacity-0 transition-opacity hover:bg-zinc-300 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteChat(chat.id);
                    }}
                  >
                    <Trash2 className="size-3.5 text-zinc-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        {/* Top bar with soul selector */}
        <div className="flex items-center gap-3 border-b border-zinc-200 px-4 py-2">
          <BotMessageSquare className="size-5 text-zinc-500" />
          <span className="text-sm font-medium text-zinc-700">Soul:</span>
          <select
            className="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm focus:border-zinc-400 focus:outline-none"
            value={selectedSoulId}
            onChange={(e) => setSelectedSoulId(e.target.value)}
            disabled={isStreaming}
          >
            <option value="">Default</option>
            {souls?.map((soul) => (
              <option key={soul.id} value={soul.id}>
                {soul.name}
                {soul.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDebugMode((d) => !d)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors',
                debugMode
                  ? 'bg-amber-100 text-amber-700 border border-amber-300'
                  : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100'
              )}
              title="Toggle debug mode"
            >
              <Bug className="size-3.5" />
              {debugMode ? 'Debug ON' : 'Debug'}
            </button>
            {activeChatId && (
              <AddToEvalSuiteButton chatId={activeChatId} />
            )}
            {activeChatId && (
              <span className="mr-8 text-xs text-zinc-400">
                Chat: {activeChatId.slice(0, 8)}...
              </span>
            )}
          </div>
        </div>

        {/* Content area: messages + BO panel */}
        <div className="flex flex-1 overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-zinc-400">
                <BotMessageSquare className="mb-4 size-12" />
                <p className="text-lg font-medium">Start a conversation</p>
                <p className="text-sm">Send a message to begin chatting with the AI.</p>
                {!isStreaming &&
                  (starterLoading ? (
                    <SuggestionPillsSkeleton className="mt-6 max-w-xl justify-center" />
                  ) : (
                    !!starterSuggestions?.length && (
                      <SuggestionPills
                        suggestions={starterSuggestions}
                        onPick={(prompt) => sendMessage(prompt)}
                        className="mt-6 max-w-xl justify-center"
                      />
                    )
                  ))}
              </div>
            ) : (
              <div className="mx-auto max-w-3xl space-y-4">
                {messages.map((msg) =>
                  msg.isActionResult ? (
                    <div key={msg.id} className="flex justify-center">
                      <div className="flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-1.5 text-xs text-green-700">
                        <CheckCircle2 className="size-3.5" />
                        <span>{msg.content}</span>
                      </div>
                    </div>
                  ) : (
                  <div
                    key={msg.id}
                    className={cn(
                      'flex',
                      msg.role === 'user' ? 'justify-end' : 'justify-start',
                    )}
                  >
                    <div
                      className={cn(
                        'max-w-[80%] rounded-lg px-4 py-2.5',
                        msg.role === 'user'
                          ? 'bg-zinc-900 text-white'
                          : 'bg-zinc-100 text-zinc-900',
                      )}
                    >
                      <div className="text-sm leading-relaxed">
                        {msg.role === 'assistant' && msg.reasoning && (
                          <details className="mb-2 rounded bg-zinc-200/60 text-zinc-600">
                            <summary className="cursor-pointer px-2 py-1 text-xs font-medium select-none">
                              Thinking
                            </summary>
                            <div className="whitespace-pre-wrap px-2 pb-2 text-xs italic">
                              {msg.reasoning}
                            </div>
                          </details>
                        )}
                        {msg.content ? (
                          msg.role === 'assistant' ? (
                            <MessageContent
                              message={msg}
                              businessObjects={businessObjects}
                              chatId={activeChatId}
                              messageId={msg.id}
                              actionStates={actionStates}
                              onActionExecuted={handleActionExecuted}
                            />
                          ) : (
                            <span className="whitespace-pre-wrap">{msg.content}</span>
                          )
                        ) : msg.thinking ? (
                          <ThinkingIndicator />
                        ) : (
                          <TypingDots />
                        )}
                      </div>
                    </div>
                  </div>
                  )
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Draft Event Panel — shown when a ticketed-event draft BO is active */}
          {businessObjects['draftEvent'] ? (
            <DraftEventPanel
              businessObjects={businessObjects}
              onClose={() => setBusinessObjects((prev) => {
                const next = { ...prev };
                delete next['draftEvent'];
                return next;
              })}
              onUpdateDraft={(patch) => {
                // Optimistically reflect the change, then persist to the draft BO
                // so executeAction sees the image IDs at create time.
                setBusinessObjects((prev) => {
                  const draft = prev['draftEvent'];
                  if (!draft) return prev;
                  return {
                    ...prev,
                    draftEvent: {
                      ...draft,
                      data: { ...(draft.data ?? {}), ...patch },
                    },
                  };
                });
                if (activeChatId) {
                  service
                    .mergeBusinessObjectData(activeChatId, 'draftEvent', patch)
                    .catch((e) => console.error('Failed to save draft images:', e));
                }
              }}
            />
          ) : (
            /* Business Object Panel */
            <BusinessObjectPanel
              businessObjects={businessObjects}
              chatId={activeChatId}
              isOpen={boPanelOpen}
              onToggle={() => setBoPanelOpen((prev) => !prev)}
            />
          )}
        </div>

        {/* Debug panel */}
        {debugMode && debugLogs.length > 0 && (
          <div className="border-t border-amber-200 bg-amber-50/50">
            <details open className="mx-auto max-w-3xl">
              <summary className="cursor-pointer select-none px-4 py-2 text-xs font-medium text-amber-700">
                Tool Queries ({debugLogs.length})
              </summary>
              <div className="max-h-48 overflow-y-auto px-4 pb-3 space-y-2">
                {debugLogs.map((log, i) => (
                  <div
                    key={i}
                    className={cn(
                      'rounded border p-2 text-xs font-mono',
                      log.type === 'tool-call'
                        ? 'border-blue-200 bg-blue-50'
                        : 'border-green-200 bg-green-50'
                    )}
                  >
                    <div className="mb-1 flex items-center gap-2">
                      <span className={cn(
                        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                        log.type === 'tool-call' ? 'bg-blue-200 text-blue-800' : 'bg-green-200 text-green-800'
                      )}>
                        {log.type === 'tool-call' ? 'call' : 'result'}
                      </span>
                      <span className="font-semibold text-zinc-700">{log.toolName}</span>
                      <span className="ml-auto text-zinc-400">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <pre className="whitespace-pre-wrap break-all text-zinc-600">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* Input bar */}
        <div className="border-t border-zinc-200 bg-white p-4">
          {!isStreaming &&
            (followUpsLoading ? (
              <SuggestionPillsSkeleton className="mx-auto mb-3 max-w-3xl" />
            ) : (
              !!followUps?.length && (
                <SuggestionPills
                  suggestions={followUps}
                  onPick={(prompt) => sendMessage(prompt)}
                  className="mx-auto mb-3 max-w-3xl"
                />
              )
            ))}
          <div className="mx-auto flex max-w-3xl items-center gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              disabled={isStreaming}
              className="flex-1"
            />
            {isStreaming ? (
              <Button
                variant="outline"
                size="icon"
                onClick={stopStreaming}
                className="shrink-0"
              >
                <Square className="size-4" />
              </Button>
            ) : (
              <Button
                variant="primary"
                size="icon"
                onClick={() => sendMessage()}
                disabled={!inputValue.trim()}
                className="shrink-0"
              >
                <Send className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AddToEvalSuiteButton({ chatId }: { chatId: string }) {
  const { data: suites } = useEvalSuites({ status: 'DRAFT' });
  const [open, setOpen] = useState(false);
  const [suiteId, setSuiteId] = useState<string>('');
  const [caseName, setCaseName] = useState<string>('');
  const [useAssistantAsExpected, setUseAssistantAsExpected] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!suiteId) {
      toastError('Pick a suite (must be DRAFT).');
      return;
    }
    setSaving(true);
    try {
      await service.addChatToEvalSuite(chatId, {
        suiteId,
        caseName: caseName || undefined,
        lastAssistantAsExpected: useAssistantAsExpected,
      });
      toastSuccess('Chat added to eval suite.');
      setOpen(false);
    } catch (e: any) {
      toastError(`Failed: ${e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
        title="Add this chat as an eval case"
      >
        <ClipboardCheck className="size-3.5" />
        Add to eval
      </button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add chat to eval suite</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Suite (DRAFT only)</label>
            <select
              className="border-input bg-background mt-1 flex h-9 w-full rounded-md border px-3 py-1 text-sm"
              value={suiteId}
              onChange={(e) => setSuiteId(e.target.value)}
            >
              <option value="">— pick —</option>
              {(suites ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Case name (optional)</label>
            <Input value={caseName} onChange={(e) => setCaseName(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useAssistantAsExpected}
              onChange={(e) => setUseAssistantAsExpected(e.target.checked)}
            />
            Use last assistant message as expected answer
          </label>
          <p className="text-xs text-amber-600">
            Chats often contain PII — the case will be tagged accordingly.
          </p>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="link">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} loading={saving}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
